/**
 * Opening a transcript at its end.
 *
 * A conversation that is opened (switch, first load, reload, a handoff into the
 * assistant panel) has to appear already positioned at the bottom — no smooth
 * scroll down, no flash of the top followed by a jump. The scroller therefore
 * sets `scrollTop` synchronously in a layout effect, before the first paint of
 * the new transcript, and keeps re-applying it instantly while late content
 * (markdown, tables, images, cards) still grows for a short settle window.
 *
 * Streaming into a conversation that is already open is not affected: there
 * the scroller keeps following the live edge only while the reader is at the
 * bottom.
 */

/** How long after opening late-growing content still re-pins the end instantly. */
export const OPEN_SETTLE_MS = 600;

/**
 * Whether the transcript identified by `openKey` has not been positioned yet.
 *
 * `openKey` is empty while there is nothing to show (no conversation, or its
 * messages are still loading), so a conversation whose messages arrive later
 * is positioned the moment they render. Guarded by the key rather than a
 * boolean, so the double effect run of StrictMode snaps only once.
 */
export function shouldSnapOnOpen(snappedKey: string | null, openKey: string | undefined): boolean {
  return !!openKey && openKey !== snappedKey;
}

/**
 * Whether a growth of the content should re-pin the end instantly (`"auto"`)
 * rather than follow with the regular behaviour: only inside the settle window
 * after opening, and only while the reader has not left the live edge.
 */
export function isInOpenSettleWindow(settleUntil: number, now: number, following: boolean): boolean {
  return following && now < settleUntil;
}

/** Puts the viewport at its very end without any animation. */
export function jumpToEnd(viewport: HTMLElement): void {
  // An inherited `scroll-behavior: smooth` would animate even a plain
  // `scrollTop` assignment — pin it to `auto` for this one write.
  const previous = viewport.style.scrollBehavior;
  viewport.style.scrollBehavior = "auto";
  viewport.scrollTop = viewport.scrollHeight;
  viewport.style.scrollBehavior = previous;
}
